import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkflow } from '../hooks/useWorkflow';
import { useDatabaseMode } from './DatabaseModeContext';

interface ModuleStatusInfo {
  workflow_id: number;
  modules: Array<{
    module_id: number;
    module_type: string;
    status: string;
    error: string | null;
    created_at: string;
    updated_at: string;
    step_number: number;
  }>;
  latest_status_by_type: Record<string, {
    module_id: number;
    module_type: string;
    status: string;
    error: string | null;
    created_at: string;
    updated_at: string;
    step_number: number;
  }>;
  step_statuses: Record<string, {
    status: string;
    has_error: boolean;
    error: string | null;
    module_type: string;
    completed: boolean;
    failed: boolean;
    in_progress: boolean;
    updated_at: string;
  }>;
  error: string | null;
}

interface WorkflowContextType {
  workflowId?: string;
  clientId?: string;
  workflowName?: string;
  clientName?: string;
  workflow?: any;
  isLoading?: boolean;
  error?: any;
  refreshTrigger?: number;
  triggerRefresh?: () => void;
  moduleStatuses?: ModuleStatusInfo | null;
  setModuleStatuses?: (statuses: ModuleStatusInfo | null) => void;
}

const WorkflowContext = createContext<WorkflowContextType>({});

export const useWorkflowContext = () => {
  const context = useContext(WorkflowContext);
  return context;
};

interface WorkflowProviderProps {
  children: React.ReactNode;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const params = useParams();
  const { isUsingDatabaseApproach } = useDatabaseMode();
  const [clientName, setClientName] = useState<string | undefined>();
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [moduleStatuses, setModuleStatuses] = useState<ModuleStatusInfo | null>(null);
  
  // Extract workflowId and clientId from URL params
  const workflowId = params.workflowId;
  const clientId = params.clientId;
  
  // Parse numeric client ID for API calls
  const numericClientId = clientId && /^\d+$/.test(clientId) 
    ? parseInt(clientId, 10) 
    : undefined;
    
  // Use the workflow hook to get workflow data
  const { workflow, isLoading: isWorkflowLoading, error: workflowError } = useWorkflow(workflowId, isUsingDatabaseApproach);
  
  // Fetch client name if we have a numeric client ID
  useEffect(() => {
    const fetchClientName = async () => {
      if (numericClientId) {
        try {
          const { clientService } = await import('../services/api/clientService');
          const client = await clientService.getClient(numericClientId);
          setClientName(client?.name);
        } catch (error) {
          console.error('Failed to fetch client name:', error);
        }
      }
    };

    fetchClientName();
  }, [numericClientId]);

  // Function to trigger a refresh of dependent components
  // Usage: const { triggerRefresh } = useWorkflowContext(); triggerRefresh();
  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const contextValue: WorkflowContextType = {
    workflowId,
    clientId,
    workflowName: workflow?.name,
    clientName,
    workflow,
    isLoading: isWorkflowLoading,
    error: workflowError,
    refreshTrigger,
    triggerRefresh,
    moduleStatuses,
    setModuleStatuses
  };

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  );
};
